import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { SkipToContent, announceToScreenReader } from '../utils/accessibility';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  Container, 
  Avatar,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Badge,
  Popover,
  FormControlLabel,
  Checkbox,
  Stack,
  Autocomplete,
  Chip
} from '@mui/material';
import SearchBar from './SearchBar';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FloatingShortcutWidget from './FloatingShortcutWidget';
import AppsModal from './AppsModal';
import MessageSidebar from './MessageSidebar';
import NotificationsMenu from './NotificationsMenu';
import { QuickBooksNotificationBell } from './quickbooks/QuickBooksNotifications';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Link as ShortcutIcon,
  Folder as FolderIcon,
  People as PeopleIcon,
  Groups as GroupsIcon,
  VpnKey as RoleIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  Image as CanvaIcon,
  Event as PlanningCenterIcon,
  Storage as SynologyIcon,
  AcUnit as DreoIcon,
  Security as LenelS2NetBoxIcon,
  PhoneIphone as MosyleBusinessIcon,
  Lock as UnifiAccessIcon,
  Router as UnifiNetworkIcon,
  Videocam as UnifiProtectIcon,
  AdminPanelSettings as AdminIcon,
  ContactPage as StaffDirectoryIcon,
  CalendarMonth as GoogleCalendarIcon,
  Description as GoogleFormsIcon,
  Person as PersonIcon,
  HelpOutline as HelpIcon,
  Notifications as NotificationsIcon,
  QuestionAnswer as FAQIcon,
  Store as MarketplaceIcon,
  NetworkCheck as RadiusIcon,
  MeetingRoom as RoomBookingIcon,
  Assignment as TaskManagementIcon,
  Build as MaintenanceSchedulingIcon,
  HomeWork as BuildingManagementIcon,
  Chat as ChatIcon,
  Apple as AppleIcon,
  MusicNote as WiimIcon,
  Thermostat as SkyportCloudIcon,
  Speaker as QsysIcon,
  Lightbulb as ColoritIcon,
  Videocam as CanonIcon,
  ConfirmationNumber as TicketIcon,
  Category as CategoryIcon,
  LocalOffer as TagIcon,
  Security as SecurityIcon,
  Apps as AppsIcon,
  Inventory as AssetManagementIcon,
  Inventory2 as AssetsIcon,
  Assessment as ReportsIcon,
  ImportExport as ImportExportIcon,
  Dashboard as AssetDashboardIcon,
  List as AssetListIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  ReportProblem as ReportProblemIcon,
  Note as NotesIcon,
  Article as ArticleIcon,
  Newspaper as NewsIcon,
  Add as AddIcon,
  ListAlt as FormsIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Google as GoogleIcon,
  Handyman,
  AttachMoney as AttachMoneyIcon,
  AccountBalance as QuickBooksIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';
import { staticMenuItems, staticMenuCategories, nestedMenuItems } from '../config/menuConfig';
import notesService from '../services/notesService';
import presenceService from '../services/presenceService';
import websocketService from '../services/websocketService';
import axios from 'axios';
import PermissionCheck from './PermissionCheck';

const ONLINE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes

const Layout = () => {
  const { user, logout, isAdmin, hasIntegration, hasPermission, isGoogleUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCollapsed, setDrawerCollapsed] = useState(() => {
    // Initialize from localStorage
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState === 'true';
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
  const [adminMenuAnchorEl, setAdminMenuAnchorEl] = useState(null);
  // Use static menu configuration only (managed menu system removed)
  const [menuItems] = useState(staticMenuItems);
  const menuLoading = false;
  const [appsModalOpen, setAppsModalOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState({});
    const [nodeOpen, setNodeOpen] = useState({});
  const [adminOpenSection, setAdminOpenSection] = useState(false);
  // Presence status dialog state
  const [customStatusOpen, setCustomStatusOpen] = useState(false);
  const [customStatusMessage, setCustomStatusMessage] = useState('');
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  
  // Offline indicator state
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Determine if the user has access to any Quick Add actions
  const hasAnyQuickAdd = !!user && (
    hasPermission('tickets:write') ||
    hasPermission('news:write') ||
    hasPermission('forms:write') ||
    hasPermission('notes:write') ||
    hasPermission('tasks:write') ||
    hasPermission('assets:write:own') || hasPermission('assets:write:any')
  );
  
  // Track active chat taskbar state to pad bottom content accordingly
  const [hasActiveChatTabs, setHasActiveChatTabs] = useState(false);
  const [chatTaskbarHeight, setChatTaskbarHeight] = useState(0);
  useEffect(() => {
    const handler = (e) => {
      const hasTabs = !!(e && e.detail && e.detail.hasTabs);
      const height = (e && e.detail && e.detail.height) || 0;
      setHasActiveChatTabs(hasTabs);
      setChatTaskbarHeight(height);
    };
    window.addEventListener('chat:activeTabs', handler);
    return () => window.removeEventListener('chat:activeTabs', handler);
  }, []);
  
  // Local presence state for header indicator
  const [presence, setPresence] = useState({ 
    status: 'available', 
    statusMessage: '', 
    lastActiveAt: new Date().toISOString(), 
    isActive: true,
    isOnline: false,
    lastSeenAt: new Date().toISOString()
  });
  const [presenceTick, setPresenceTick] = useState(0);
  const statusToColor = (status) => {
    switch (status) {
      case 'available': return 'success';
      case 'away': return 'warning';
      case 'lunch': return 'info';
      case 'busy': return 'error';
      case 'custom': return 'info';
      default: return 'default';
    }
  };

  // Responsive layout: persistent sidebar on desktop, temporary drawer on mobile
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const drawerWidth = drawerCollapsed ? 72 : 300;
  const collapsedDrawerWidth = 72;
  const expandedDrawerWidth = 300;

  useEffect(() => {
    setDrawerOpen(isDesktop ? true : false);
  }, [isDesktop]);

  // Quick Add Note dialog state
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [addNoteSubmitting, setAddNoteSubmitting] = useState(false);
  const [addNoteError, setAddNoteError] = useState(null);
  const [addNoteData, setAddNoteData] = useState({
    title: '',
    content: '',
    isCompleted: false,
    isPinned: false,
    isTodo: false,
    tags: [],
    categories: []
  });



  // Initialize WebSocket and presence trackers when user is available
  useEffect(() => {
    if (!user) return;
    try {
      websocketService.connect().then(() => {
        // Authenticate with user ID after connection
        websocketService.setUserId(user.id);
      }).catch(err => console.warn('WebSocket connect failed:', err));
    } catch (e) {
      console.warn('WebSocket connect threw:', e);
    }
    presenceService.init();
    return () => {
      presenceService.destroy();
    };
  }, [user]);

  // Subscribe to notifications events via WebSocket
  useEffect(() => {
    if (!user) return;
    
    let mounted = true;

    const handleNotification = (payload) => {
      if (!mounted) return;
      try {
        // Increment unread count on each notification event
        setUnreadNotifications(prev => prev + 1);
      } catch (error) {
        console.warn('Error handling notification:', error);
      }
    };

    // Ensure connection then subscribe
    websocketService.connect().then(() => {
      if (mounted) {
        websocketService.subscribe('notification');
        websocketService.addEventListener('notification', handleNotification);
      }
    }).catch(() => {
      // Non-fatal in dev if WS not connected
    });

    return () => {
      mounted = false;
      websocketService.removeEventListener('notification', handleNotification);
      websocketService.unsubscribe('notification');
    };
  }, [user]);

  // Fetch initial presence for header indicator
  useEffect(() => {
    if (!user) return;
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get('/api/users/me');
        const u = res.data || {};
        const p = (u && u.presence) || {};
        if (mounted) {
          setPresence({
            status: p.status || 'available',
            statusMessage: p.statusMessage || '',
            lastActiveAt: p.lastActiveAt || new Date().toISOString(),
            isActive: typeof u.isActive === 'boolean' ? u.isActive : true
          });
        }
      } catch (e) {
        // Non-fatal
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  // Subscribe to presence updates for current user
  useEffect(() => {
    if (!user) return;
    
    let mounted = true;

    const handlePresence = (payload) => {
      if (!mounted || !payload || !payload.userId) return;
      if (payload.userId !== user.id) return;
      try {
        const p = payload.presence || {};
        setPresence(prev => ({
          ...prev,
          status: p.status || prev.status,
          statusMessage: typeof p.statusMessage === 'string' ? p.statusMessage : prev.statusMessage,
          lastActiveAt: p.lastActiveAt || prev.lastActiveAt,
          isOnline: typeof p.isOnline === 'boolean' ? p.isOnline : prev.isOnline
        }));
      } catch (error) {
        console.warn('Error handling presence update:', error);
      }
    };

    const handleUserOnlineStatus = (payload) => {
      if (!mounted || !payload || !payload.userId) return;
      if (payload.userId !== user.id) return;
      try {
        setPresence(prev => ({
          ...prev,
          isOnline: payload.isOnline,
          status: payload.status || prev.status,
          statusMessage: payload.statusMessage || prev.statusMessage,
          lastSeenAt: payload.lastSeenAt || prev.lastSeenAt || new Date().toISOString()
        }));
      } catch (error) {
        console.warn('Error handling online status update:', error);
      }
    };

    websocketService.connect().then(() => {
      if (mounted) {
        websocketService.addEventListener('presence', handlePresence);
        websocketService.addEventListener('userOnlineStatus', handleUserOnlineStatus);
      }
    }).catch(() => {});

    return () => {
      mounted = false;
      websocketService.removeEventListener('presence', handlePresence);
      websocketService.removeEventListener('userOnlineStatus', handleUserOnlineStatus);
    };
  }, [user]);

  // Ticker to refresh online badge visibility roughly each minute
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => setPresenceTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, [user]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerCollapse = useCallback(() => {
    setDrawerCollapsed(prev => {
      const newState = !prev;
      localStorage.setItem('sidebarCollapsed', newState.toString());
      return newState;
    });
  }, []);

  const handleProfileMenuOpen = (event) => {
    try {
      setAnchorEl(event.currentTarget);
    } catch (error) {
      console.warn('Error opening profile menu:', error);
    }
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddMenuOpen = (event) => {
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchorEl(null);
  };

  const openQuickAddNote = () => {
    setAddMenuAnchorEl(null);
    setAddNoteError(null);
    setAddNoteData({
      title: '',
      content: '',
      isCompleted: false,
      isPinned: false,
      isTodo: false,
      tags: [],
      categories: []
    });
    setAddNoteOpen(true);
  };

  // Handle input change for add note form
  const handleAddNoteInputChange = (e) => {
    const { name, value, checked } = e.target;
    setAddNoteData(prev => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value
    }));
  };

  const handleAddNoteSubmit = async () => {
    try {
      setAddNoteSubmitting(true);
      setAddNoteError(null);
      await notesService.createNote(addNoteData);
      setAddNoteSubmitting(false);
      setAddNoteOpen(false);
    } catch (err) {
      console.error('Error creating note from quick add:', err);
      setAddNoteError('Failed to create note. Please try again.');
      setAddNoteSubmitting(false);
    }
  };
  
  const handleAdminMenuOpen = (event) => {
    setAdminMenuAnchorEl(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchorEl(null);
  };

  const handleSearchOpen = (event) => {
    setSearchAnchorEl(event.currentTarget);
    setSearchPopupOpen(true);
  };

  const handleSearchClose = () => {
    setSearchPopupOpen(false);
    setSearchAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  const handleEditProfile = () => {
    navigate(`/staff-directory/users/${user.id}`);
    handleProfileMenuClose();
  };

  // Filter menu items by type
  const navItems = menuItems.filter(item => item.type === 'regular');
  const integrationItems = menuItems.filter(item => item.type === 'integration');
  const adminNavItems = menuItems.filter(item => item.type === 'admin');

  // Helper to check if a menu path is active for current location (supports tab/sub-routes)
  const isPathActive = useCallback((itemPath) => {
    const normalize = (p) => {
      if (!p) return '/';
      return p.length > 1 && p.endsWith('/') ? p.replace(/\/+$/, '') : p;
    };
    const base = normalize(itemPath);
    const current = normalize(location.pathname);
    if (current === base) return true;
    if (base === '/') return current === '/';
    // Special case: Procurement Dashboard should only be active on exact dashboard routes
    // Do not highlight it for other procurement subroutes like /procurement/vendors, /procurement/requests, etc.
    if (base === '/procurement') {
      return current === '/procurement' || current === '/procurement/dashboard';
    }
    // Special case: Tasks should only be active on exact /tasks route, not /tasks/recurring
    if (base === '/tasks') {
      return current === '/tasks';
    }
    // Special case: Assets dashboard should only be active on exact /assets route
    // Do not highlight it for asset subroutes like /assets/list, /assets/requests, etc.
    if (base === '/assets') {
      return current === '/assets';
    }
    return current.startsWith(base + '/');
  }, [location.pathname]);

  // Helper function to get category icon
  const getCategoryIcon = (categoryName) => {
    const category = staticMenuCategories.find(cat => 
      cat.name.toLowerCase() === categoryName.toLowerCase()
    );
    return category ? category.icon : <CategoryIcon />;
  };




  // Helper function to render icon based on icon name or use custom icon
  const renderIcon = (item) => {
    if (item.customIcon) {
      return <Avatar src={item.customIcon} sx={{ width: 24, height: 24 }} />;
    }
    
    // If using database items, the icon is a string name
    if (typeof item.icon === 'string') {
      // Map icon names to Material-UI icons
      const iconMap = {
        'dashboard': <DashboardIcon />,
        'link': <ShortcutIcon />,
        'folder': <FolderIcon />,
        'people': <PeopleIcon />,
        'groups': <GroupsIcon />,
        'vpn_key': <RoleIcon />,
        'settings': <SettingsIcon />,
        'image': <CanvaIcon />,
        'event': <PlanningCenterIcon />,
        'storage': <SynologyIcon />,
        'ac_unit': <DreoIcon />,
        'security': <SecurityIcon />,
        'phone_iphone': <MosyleBusinessIcon />,
        'lock': <UnifiAccessIcon />,
        'router': <UnifiNetworkIcon />,
        'videocam': <UnifiProtectIcon />,
        'admin_panel_settings': <AdminIcon />,
        'contact_page': <StaffDirectoryIcon />,
        'calendar_month': <GoogleCalendarIcon />,
        'description': <GoogleFormsIcon />,
        'person': <PersonIcon />,
        'help_outline': <HelpIcon />,
        'question_answer': <FAQIcon />,
        'network_check': <RadiusIcon />,
        'meeting_room': <RoomBookingIcon />,
        'assignment': <TaskManagementIcon />,
        'build': <MaintenanceSchedulingIcon />,
        'home_work': <BuildingManagementIcon />,
        'apple': <AppleIcon />,
        'music_note': <WiimIcon />,
        'thermostat': <SkyportCloudIcon />,
        'speaker': <QsysIcon />,
        'lightbulb': <ColoritIcon />,
        'confirmation_number': <TicketIcon />,
        'category': <CategoryIcon />,
        'local_offer': <TagIcon />,
        'menu': <MenuIcon />,
        'apps': <AppsIcon />,
        'inventory2': <AssetsIcon />,
        'assessment': <ReportsIcon />,
        'import_export': <ImportExportIcon />,
        'list': <AssetListIcon />,
        'assignment_turned_in': <AssignmentTurnedInIcon />,
        'report_problem': <ReportProblemIcon />,
        'article': <ArticleIcon />,
        'newspaper': <NewsIcon />,
        'note': <NotesIcon />,
        'account_balance': <QuickBooksIcon />
      };
      
      return iconMap[item.icon] || <ShortcutIcon />;
    }
    
    // If using static items, the icon is already a React element
    return item.icon;
  };

  // Use static menu configuration directly
  const displayNavItems = navItems;
  const displayIntegrationItems = integrationItems;
  const displayAdminNavItems = adminNavItems;

  // Helpers for submenu grouping
  const getItemText = (item) => item.friendlyName || item.title || item.text;
  const getItemPermission = (item) => item.requiredPermission || item.permission;
  const getItemCategories = (item) => item.categories || [];
  const isAssetItem = (item) => {
    const path = (item.path || '').toLowerCase();
    return path === '/assets' || path.startsWith('/assets/');
  };
  const isGoogleItem = (item) => {
    const cats = getItemCategories(item);
    const text = (getItemText(item) || '').toLowerCase();
    const path = (item.path || '').toLowerCase();
    return (
      cats.includes('Google Services') ||
      text.includes('google') ||
      path.startsWith('/google') ||
      path === '/drive'
    );
  };

  // Build Google submenu items from both nav and integrations, filter by permission and dedupe by path
  const googleMap = new Map();

  // Permission predicate for items
  const passesPermission = (item) => {
    const perm = getItemPermission(item);
    return !perm || hasPermission(perm);
  };

  const shouldHideForGoogleUser = (item) => (isGoogleItem(item) && !isGoogleUser());

  // Determine if a nested node (item or parent) contains the active route
  const nodeContainsActive = (node) => {
    if (!node) return false;
    if (node.path && isPathActive(node.path)) return true;
    if (Array.isArray(node.children)) {
      return node.children.some((child) => nodeContainsActive(child));
    }
    return false;
  };

  // Determine if a node should be shown after applying Google user and permission filters
  const shouldShowNode = (node) => {
    if (shouldHideForGoogleUser(node)) return false;
    const hasPathAndPermitted = !!node.path && passesPermission(node);
    if (hasPathAndPermitted) return true;
    if (Array.isArray(node.children)) {
      return node.children.some((child) => shouldShowNode(child));
    }
    return false;
  };

  const renderNestedNode = (node, depth = 0) => {
    if (!shouldShowNode(node)) return null;
    const paddingLeft = drawerCollapsed ? 0 : (1 + depth) * 1; // rem units
    const isParent = Array.isArray(node.children) && node.children.length > 0;
    const key = node._id || `${getItemText(node)}-${depth}`;

    if (isParent) {
      const open = !!nodeOpen[key];
      const title = getItemText(node);
      const anyChildActive = nodeContainsActive(node);
      return (
        <Box key={key} sx={{ ml: drawerCollapsed ? 0 : depth ? 2 : 0 }}>
          <Box
            onClick={(e) => { e.stopPropagation(); setNodeOpen(prev => ({ ...prev, [key]: !prev[key] })); }}
            sx={{
              display: 'flex', alignItems: 'center',
              padding: drawerCollapsed ? '0.75rem' : '0.5rem 1rem',
              margin: drawerCollapsed ? '0.25rem auto' : '0.25rem 0.5rem',
              justifyContent: drawerCollapsed ? 'center' : 'flex-start',
              width: drawerCollapsed ? '48px' : 'auto',
              borderRadius: '8px', color: anyChildActive ? '#2563eb' : '#4a5568',
              background: anyChildActive ? 'rgba(37, 99, 235, 0.1)' : 'transparent', cursor: 'pointer',
              transition: 'all 0.3s ease', pl: drawerCollapsed ? 0 : paddingLeft,
              '&:hover': { background: 'rgba(37, 99, 235, 0.1)', color: '#2563eb' }
            }}
          >
            {renderIcon(node)}
            {!drawerCollapsed && <Typography sx={{ ml: 2, fontSize: '0.875rem', fontWeight: 500, flexGrow: 1 }}>{title}</Typography>}
            {!drawerCollapsed && (open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
          </Box>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ pl: 0 }}>
              {(node.children || [])
                .filter((child) => shouldShowNode(child))
                .sort(sortByOrderThenText)
                .map((child) => renderNestedNode(child, Math.min(depth + 1, 2))) /* limit visual indent to 3 levels */}
            </Box>
          </Collapse>
        </Box>
      );
    }

    // Leaf link
    const itemText = getItemText(node);
    const itemPath = node.path;
    const isActive = isPathActive(itemPath);
    return (
      <Box
        key={key}
        component={Link}
        to={itemPath}
        sx={{
          display: 'flex', alignItems: 'center',
          padding: drawerCollapsed ? '0.75rem' : '0.5rem 1rem',
          margin: drawerCollapsed ? '0.25rem auto' : '0.25rem 0.5rem',
          justifyContent: drawerCollapsed ? 'center' : 'flex-start',
          width: drawerCollapsed ? '48px' : 'auto',
          borderRadius: '8px', color: isActive ? '#2563eb' : '#4a5568',
          background: isActive ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
          textDecoration: 'none', fontSize: '0.875rem', opacity: isActive ? 1 : 0.8,
          transition: 'all 0.3s ease', pl: drawerCollapsed ? 0 : paddingLeft,
          '&:hover': { background: 'rgba(37, 99, 235, 0.1)', color: '#2563eb' }
        }}
      >
        {renderIcon(node)}
        {!drawerCollapsed && <Typography sx={{ ml: 2, fontSize: 'inherit' }}>{itemText}</Typography>}
      </Box>
    );
  };
  [...displayNavItems, ...displayIntegrationItems]
    .filter((it) => isGoogleItem(it))
    .forEach((it) => {
      const perm = getItemPermission(it);
      if (isGoogleUser() && (!perm || hasPermission(perm))) {
        googleMap.set(it.path, it);
      }
    });
  const googleItems = Array.from(googleMap.values()).sort((a, b) => getItemText(a).localeCompare(getItemText(b)));

  // Build Assets submenu items from nav items, filter by permission and dedupe by path
  const assetMap = new Map();
  displayNavItems
    .filter((it) => isAssetItem(it))
    .forEach((it) => {
      const perm = getItemPermission(it);
      if (!perm || hasPermission(perm)) {
        assetMap.set(it.path, it);
      }
    });
  const assetItems = Array.from(assetMap.values()).sort((a, b) => {
    const ao = (a.order ?? 0);
    const bo = (b.order ?? 0);
    if (ao !== bo) return ao - bo;
    return getItemText(a).localeCompare(getItemText(b));
  });

  // Exclude Google items from the main lists to avoid duplication
  const displayNavItemsFiltered = displayNavItems.filter((it) => !isGoogleItem(it) && !isAssetItem(it));
  const displayIntegrationItemsFiltered = displayIntegrationItems.filter((it) => !isGoogleItem(it));

  // Consistent sorting helper
  const sortByOrderThenText = (a, b) => {
    // Always pin Dashboard at the very top regardless of order/text
    if ((a.path || '').toLowerCase() === '/dashboard') return -1;
    if ((b.path || '').toLowerCase() === '/dashboard') return 1;
    const ao = (a.order ?? 0);
    const bo = (b.order ?? 0);
    if (ao !== bo) return ao - bo;
    return getItemText(a).localeCompare(getItemText(b));
  };

  // Sorted lists to reduce confusion
  const mainItemsSorted = [...displayNavItems].sort(sortByOrderThenText);
  const integrationItemsSorted = [...displayIntegrationItems].sort(sortByOrderThenText);
  // IT grouping for integrations
  const itIntegrationItemsSorted = integrationItemsSorted.filter((it) => getItemCategories(it).includes('IT'));
  const otherIntegrationItemsSorted = integrationItemsSorted.filter((it) => !getItemCategories(it).includes('IT'));

  // Admin items for sidebar section (filtered by permission)
  const adminItems = isAdmin() ? displayAdminNavItems.filter((item) => {
    // Skip Google items for local users regardless of permissions
    if (isGoogleItem(item) && !isGoogleUser()) {
      return false;
    }
    const perm = getItemPermission(item);
    return (!perm || hasPermission(perm)) && !item.isDivider && !item.isHeader;
  }) : [];
  const adminItemsSorted = [...adminItems].sort(sortByOrderThenText);

  // Build nested category map from hierarchical menu items for sidebar (supports multi-level nesting)
  const nestedCategoryMap = {};
  nestedMenuItems.forEach((it) => {
    // Skip Google items for local users
    if (isGoogleItem(it) && !isGoogleUser()) {
      return;
    }
    const cats = getItemCategories(it) || [];
    const nonCoreCats = cats.filter((c) => c && c !== 'Core Features');
    if (nonCoreCats.length > 0) {
      nonCoreCats.forEach((cat) => {
        if (!nestedCategoryMap[cat]) nestedCategoryMap[cat] = [];
        nestedCategoryMap[cat].push(it);
      });
    }
  });
  const categoryNamesSorted = Object.keys(nestedCategoryMap).sort((a, b) => {
    const catA = staticMenuCategories.find(cat => cat.name === a);
    const catB = staticMenuCategories.find(cat => cat.name === b);
    const orderA = catA ? (catA.order ?? 999) : 999;
    const orderB = catB ? (catB.order ?? 999) : 999;
    if (orderA !== orderB) return orderA - orderB;
    return a.localeCompare(b);
  });
  const topLevelMainItems = mainItemsSorted.filter((it) => {
    // Skip Google items for local users
    if (isGoogleItem(it) && !isGoogleUser()) {
      return false;
    }
    
    const cats = getItemCategories(it);
    const nonCoreCats = cats.filter((c) => c && c !== 'Core Features');
    return nonCoreCats.length === 0;
  });

  // Identify dashboard item and a list without it for separate rendering
  const dashboardItem = topLevelMainItems.find((it) => (it.path || '').toLowerCase() === '/dashboard');
  const topLevelMainItemsWithoutDashboard = topLevelMainItems.filter((it) => (it.path || '').toLowerCase() !== '/dashboard');

  // Auto-open category groups and admin section based on active route
  useEffect(() => {
    const currentPath = (location.pathname || '').toLowerCase();
    const newOpen = { ...categoryOpen };
    // Open any category that contains an item whose path is active (supports tab/sub-routes)
    categoryNamesSorted.forEach((cat) => {
      const nodes = nestedCategoryMap[cat] || [];
      newOpen[cat] = nodes.some((node) => nodeContainsActive(node));
    });
    setCategoryOpen(newOpen);

    const shouldOpenAdmin = currentPath.startsWith('/admin');
    setAdminOpenSection(shouldOpenAdmin);
  }, [location.pathname, categoryNamesSorted.length]);

  // Determine required permission for the current route based on managed menu items
  const currentPathLower = (location.pathname || '').toLowerCase();
  const allGuardItems = [
    ...displayNavItems,
    ...displayIntegrationItems,
    ...adminItemsSorted,
    ...googleItems,
    ...assetItems
  ];
  let routeRequiredPermission = null;
  let bestMatchLength = -1;
  for (const it of allGuardItems) {
    const p = (it.path || '').toLowerCase();
    if (!p || it.isDivider || it.isHeader) continue;
    const isExact = currentPathLower === p;
    const isPrefix = currentPathLower.startsWith(p.endsWith('/') ? p : (p + '/'));
    if (isExact || isPrefix) {
      const perm = getItemPermission(it);
      if (perm && p.length > bestMatchLength) {
        bestMatchLength = p.length;
        routeRequiredPermission = perm;
      }
    }
  }

  const drawer = (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }} role="navigation" aria-label="Primary" onClick={!isDesktop ? handleDrawerToggle : undefined}>
      {/* Logo Section - Compact */}
      <Box sx={{ 
        padding: drawerCollapsed ? '2.75rem 0.5rem 0.75rem' : '1rem 1rem',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
        position: 'relative',
        transition: 'padding 0.3s ease'
      }}>
        {/* Collapse/Expand Toggle Button - Only on Desktop */}
        {isDesktop && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDrawerCollapse();
            }}
            size="small"
            sx={{
              position: 'absolute',
              right: 8,
              transform: 'none',
              top: 8,
              backgroundColor: 'rgba(37, 99, 235, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(37, 99, 235, 0.2)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            {drawerCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
        <Logo height={drawerCollapsed ? 32 : 48} variant="full" showText={false} sx={{ mb: drawerCollapsed ? 0.5 : 1, transition: 'all 0.3s ease' }} />
        {!drawerCollapsed && (
          <>
            <Typography variant="h6" component="h1" sx={{ 
              color: '#374151', 
              fontSize: '0.95rem', 
              fontWeight: 700, 
              lineHeight: 1.2, 
              mb: 0.25,
              opacity: drawerCollapsed ? 0 : 1,
              transition: 'opacity 0.3s ease'
            }}>
              Christian Student Fellowship
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#6b7280', 
              fontSize: '0.75rem',
              fontWeight: 500,
              opacity: drawerCollapsed ? 0 : 1,
              transition: 'opacity 0.3s ease'
            }}>
              Portal
            </Typography>
          </>
        )}
      </Box>
      
      {menuLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {/* Navigation Menu */}
          <Box sx={{ padding: '1rem 0', flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
            {/* Pinned Dashboard at very top */}
            {dashboardItem && (() => {
              const item = dashboardItem;
              const itemText = getItemText(item);
              const itemPath = item.path;
              const itemPermission = getItemPermission(item);
              const isActive = isPathActive(itemPath);
              return (!itemPermission || hasPermission(itemPermission)) && (
                <Box
                  key="dashboard-pinned"
                  component={Link}
                  to={itemPath}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: drawerCollapsed ? '0.75rem' : '1rem 1.5rem',
                    margin: drawerCollapsed ? '0.25rem auto' : '0.5rem 1rem',
                    justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                    width: drawerCollapsed ? '48px' : 'auto',
                    borderRadius: '12px',
                    color: isActive ? '#2563eb' : '#4a5568',
                    background: isActive ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(37, 99, 235, 0.1)',
                      color: '#2563eb',
                      transform: 'translateX(5px)'
                    }
                  }}
                >
                  {renderIcon(item)}
                  {!drawerCollapsed && <Typography sx={{ ml: 2, fontSize: 'inherit' }}>{itemText}</Typography>}
                </Box>
              );
            })()}

            {/* Category groups from managed menu system */}
            {categoryNamesSorted.map((cat) => {
              const permittedNodes = (nestedCategoryMap[cat] || []).filter((node) => shouldShowNode(node));
              if (permittedNodes.length === 0) return null; // Hide empty groups
              return (
                <Box key={cat} sx={{ margin: drawerCollapsed ? '0.5rem 0' : '0.5rem 1rem', borderRadius: '12px', overflow: 'hidden' }}>
                  <Box
                    onClick={(e) => { e.stopPropagation(); setCategoryOpen(prev => ({ ...prev, [cat]: !prev[cat] })); }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: drawerCollapsed ? '0.75rem' : '1rem 1.5rem',
                      margin: drawerCollapsed ? '0 auto' : 0,
                      width: drawerCollapsed ? '48px' : 'auto',
                      color: '#4a5568',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                      borderRadius: drawerCollapsed ? '8px' : 0,
                      '&:hover': {
                        background: 'rgba(37, 99, 235, 0.1)',
                        color: '#2563eb',
                        transform: drawerCollapsed ? 'none' : 'translateX(5px)'
                      }
                    }}
                  >
                    {React.cloneElement(getCategoryIcon(cat), { sx: { mr: drawerCollapsed ? 0 : 2, fontSize: '1.25rem' } })}
                    {!drawerCollapsed && (
                      <>
                        <Typography sx={{ flexGrow: 1, fontSize: '0.95rem', fontWeight: 500 }}>{cat}</Typography>
                        {categoryOpen[cat] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </>
                    )}
                  </Box>
                  <Collapse in={!!categoryOpen[cat]} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 2 }}>
                      {permittedNodes
                        .sort(sortByOrderThenText)
                        .map((node) => renderNestedNode(node))}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}

            {/* Top Level Main Items */}
            {topLevelMainItemsWithoutDashboard.map((item) => {
              const itemText = item.friendlyName || item.title || item.text;
              const itemPath = item.path;
              const itemPermission = item.requiredPermission || item.permission;
              const isActive = isPathActive(itemPath);
              
              // Skip Google items for local users regardless of permissions
              if (isGoogleItem(item) && !isGoogleUser()) {
                return null;
              }
              
              return (!itemPermission || hasPermission(itemPermission)) && (
                <Box
                  key={itemText}
                  component={Link}
                  to={itemPath}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: drawerCollapsed ? '0.75rem' : '1rem 1.5rem',
                    margin: drawerCollapsed ? '0.25rem auto' : '0.5rem 1rem',
                    justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                    width: drawerCollapsed ? '48px' : 'auto',
                    borderRadius: '12px',
                    color: isActive ? '#2563eb' : '#4a5568',
                    background: isActive ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 500,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(37, 99, 235, 0.1)',
                      color: '#2563eb',
                      transform: drawerCollapsed ? 'none' : 'translateX(5px)'
                    }
                  }}
                >
                  {renderIcon(item)}
                  {!drawerCollapsed && <Typography sx={{ ml: 2, fontSize: 'inherit' }}>{itemText}</Typography>}
                </Box>
              );
            })}

          {user && (
            <>
              {/* IT Section (subset of integrations) */}
              {itIntegrationItemsSorted.length > 0 && (
                <Box sx={{ 
                  padding: drawerCollapsed ? '1.5rem 0.5rem 1rem 0.5rem' : '1.5rem 1rem 1rem 1rem',
                  borderTop: '1px solid rgba(0, 0, 0, 0.05)',
                  mt: 2
                }}>
                  {!drawerCollapsed && (
                    <Typography variant="subtitle2" sx={{ 
                      color: '#718096', 
                      fontSize: '0.75rem', 
                      fontWeight: 600, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.1em',
                      mb: 1.5 
                    }}>
                      IT
                    </Typography>
                  )}
                  
                  {itIntegrationItemsSorted
                    .filter(item => {
                      // Skip Google items for local users regardless of permissions
                      if (isGoogleItem(item) && !isGoogleUser()) {
                        return false;
                      }
                      const itemPermission = item.requiredPermission || item.permission;
                      return !itemPermission || hasPermission(itemPermission);
                    })
                    .map((item) => {
                      const itemText = item.friendlyName || item.title || item.text;
                      const itemPath = item.path;
                      const isActive = isPathActive(itemPath);
                      
                      return (
                        <Box
                          key={itemText}
                          component={Link}
                          to={itemPath}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: drawerCollapsed ? '0.75rem' : '0.75rem 1rem',
                            margin: drawerCollapsed ? '0.25rem auto' : '0.25rem 0',
                            justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                            width: drawerCollapsed ? '48px' : 'auto',
                            borderRadius: '8px',
                            color: isActive ? '#2563eb' : '#4a5568',
                            background: isActive ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                            opacity: isActive ? 1 : 0.8,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'rgba(37, 99, 235, 0.1)',
                              color: '#2563eb',
                              transform: drawerCollapsed ? 'none' : 'translateX(3px)'
                            }
                          }}
                        >
                          {renderIcon(item)}
                          {!drawerCollapsed && <Typography sx={{ ml: 2, fontSize: 'inherit' }}>{itemText}</Typography>}
                        </Box>
                      );
                    })}
                </Box>
              )}

              {/* Integrations Section */}
              {otherIntegrationItemsSorted.length > 0 && (
                <Box sx={{ 
                  padding: drawerCollapsed ? '1.5rem 0.5rem 1rem 0.5rem' : '1.5rem 1rem 1rem 1rem',
                  borderTop: '1px solid rgba(0, 0, 0, 0.05)',
                  mt: 2
                }}>
                  {!drawerCollapsed && (
                    <Typography variant="subtitle2" sx={{ 
                      color: '#718096', 
                      fontSize: '0.75rem', 
                      fontWeight: 600, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.1em',
                      mb: 1.5 
                    }}>
                      Integrations
                    </Typography>
                  )}
                  
                  {otherIntegrationItemsSorted
                    .filter(item => {
                      // Skip Google items for local users regardless of permissions
                      if (isGoogleItem(item) && !isGoogleUser()) {
                        return false;
                      }
                      const itemPermission = item.requiredPermission || item.permission;
                      return !itemPermission || hasPermission(itemPermission);
                    })
                    .map((item) => {
                      const itemText = item.friendlyName || item.title || item.text;
                      const itemPath = item.path;
                      const isActive = isPathActive(itemPath);
                      
                      return (
                        <Box
                          key={itemText}
                          component={Link}
                          to={itemPath}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: drawerCollapsed ? '0.75rem' : '0.75rem 1rem',
                            margin: drawerCollapsed ? '0.25rem auto' : '0.25rem 0',
                            justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                            width: drawerCollapsed ? '48px' : 'auto',
                            borderRadius: '8px',
                            color: isActive ? '#2563eb' : '#4a5568',
                            background: isActive ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                            opacity: isActive ? 1 : 0.8,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'rgba(37, 99, 235, 0.1)',
                              color: '#2563eb',
                              transform: drawerCollapsed ? 'none' : 'translateX(3px)'
                            }
                          }}
                        >
                          {renderIcon(item)}
                          {!drawerCollapsed && <Typography sx={{ ml: 2, fontSize: 'inherit' }}>{itemText}</Typography>}
                        </Box>
                      );
                    })}
                </Box>
              )}

              {/* Administration Section */}
              {adminItemsSorted.length > 0 && (
                <Box sx={{ 
                  padding: drawerCollapsed ? '1.5rem 0.5rem 1rem 0.5rem' : '1.5rem 1rem 1rem 1rem',
                  borderTop: '1px solid rgba(0, 0, 0, 0.05)',
                  mt: 2
                }}>
                  {!drawerCollapsed && (
                    <Typography variant="subtitle2" sx={{ 
                      color: '#718096', 
                      fontSize: '0.75rem', 
                      fontWeight: 600, 
                      textTransform: 'uppercase', 
                      letterSpacing: '0.1em',
                      mb: 1.5 
                    }}>
                      Administration
                    </Typography>
                  )}
                  
                  <Box
                    onClick={(e) => { e.stopPropagation(); setAdminOpenSection(!adminOpenSection); }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: drawerCollapsed ? '0.75rem' : '1rem',
                      margin: drawerCollapsed ? '0.25rem auto' : '0.5rem 0',
                      justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                      width: drawerCollapsed ? '48px' : 'auto',
                      borderRadius: '8px',
                      color: '#4a5568',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'rgba(37, 99, 235, 0.1)',
                        color: '#2563eb',
                        transform: drawerCollapsed ? 'none' : 'translateX(3px)'
                      }
                    }}
                  >
                    <AdminIcon sx={{ mr: drawerCollapsed ? 0 : 2, fontSize: '1.25rem' }} />
                    {!drawerCollapsed && (
                      <>
                        <Typography sx={{ flexGrow: 1, fontSize: '0.875rem', fontWeight: 500 }}>Admin Tools</Typography>
                        {adminOpenSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </>
                    )}
                  </Box>
                  
                  <Collapse in={adminOpenSection} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: drawerCollapsed ? 0 : 1, mt: 1 }}>
                      {adminItemsSorted.map((item) => {
                        const itemText = getItemText(item);
                        const itemPath = item.path;
                        const isActive = isPathActive(itemPath);

                        return (
                          <Box
                            key={itemText}
                            component={Link}
                            to={itemPath}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: drawerCollapsed ? '0.75rem' : '0.75rem 1rem',
                              margin: drawerCollapsed ? '0.25rem auto' : '0.25rem 0',
                              justifyContent: drawerCollapsed ? 'center' : 'flex-start',
                              width: drawerCollapsed ? '48px' : 'auto',
                              borderRadius: '8px',
                              color: isActive ? '#2563eb' : '#4a5568',
                              background: isActive ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              opacity: isActive ? 1 : 0.8,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: 'rgba(37, 99, 235, 0.1)',
                                color: '#2563eb',
                                transform: drawerCollapsed ? 'none' : 'translateX(3px)'
                              }
                            }}
                          >
                            {renderIcon(item)}
                            {!drawerCollapsed && <Typography sx={{ ml: 2, fontSize: 'inherit' }}>{itemText}</Typography>}
                          </Box>
                        );
                      })}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </>
          )}
          </Box>

          {/* User Profile Section at Bottom */}
          {user && (
            <Box sx={{ 
              padding: drawerCollapsed ? '1rem 0.5rem' : '1rem',
              borderTop: '1px solid rgba(0, 0, 0, 0.05)',
              mt: '2rem', // Add more spacing above user section
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0,
              backgroundColor: (theme) => theme.palette.grey[100]
            }}>
              <IconButton
                onClick={handleProfileMenuOpen}
                size="small"
                sx={{ p: 0 }}
                aria-controls="profile-menu"
                aria-haspopup="true"
              >
                {(() => {
                  const status = presence && presence.status;
                  const s = (status || '').toString().toLowerCase();
                  const indicatorColor =
                    (s === 'active' || s === 'available' || s === 'online') ? 'success.main' :
                    (s === 'busy' || s === 'dnd' || s === 'do_not_disturb') ? 'error.main' :
                    (s === 'away' || s === 'idle' || s === 'offline') ? 'grey.400' :
                    'warning.main';
                  return (
                    <Box sx={{ position: 'relative', lineHeight: 0 }}>
                      <Avatar 
                        alt={user.name} 
                        src={user.avatar ? `/api/auth/avatar-proxy?url=${encodeURIComponent(user.avatar)}` : undefined} 
                        sx={{ width: 40, height: 40 }}
                      />
                      <Box
                        aria-label={status || 'unknown'}
                        sx={{
                          position: 'absolute',
                          bottom: 1,
                          right: 1,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: indicatorColor,
                          boxShadow: '0 0 0 2px #fff',
                        }}
                      />
                    </Box>
                  );
                })()}
              </IconButton>
              {!drawerCollapsed && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3748', lineHeight: 1.2 }}>
                    {user.name || user.email.split('@')[0]}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#718096', display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                    {user.email}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #3b82f6 100%)'
    }}>
      {user && (
      <AppBar position="static" sx={{ ...(user && isDesktop ? { width: `calc(100% - ${drawerWidth}px)`, ml: `${drawerWidth}px` } : {}) }}>
        <Toolbar>
          {user && !isDesktop && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          {/* FloatingShortcutWidget integrated into header - left side */}
          {user && (
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <FloatingShortcutWidget inHeader={true} />
            </Box>
          )}
          
          {user ? (
            <>
              {/* Search popup icon */}
              <IconButton
                color="inherit"
                size="small"
                onClick={handleSearchOpen}
                aria-label="search"
                sx={{ ml: 1 }}
              >
                <SearchIcon />
              </IconButton>
              {isAdmin() && (
                <IconButton
                  color="inherit"
                  size="small"
                  sx={{ ml: 2 }}
                  aria-label="admin"
                  aria-controls="admin-menu"
                  aria-haspopup="true"
                  onClick={handleAdminMenuOpen}
                >
                  <AdminIcon />
                </IconButton>
              )}
              {hasAnyQuickAdd && (
                <IconButton
                  color="inherit"
                  size="small"
                  sx={{ ml: 2 }}
                  aria-label="add"
                  aria-controls="add-menu"
                  aria-haspopup="true"
                  onClick={handleAddMenuOpen}
                >
                  <AddIcon />
                </IconButton>
              )}
              <NotificationsMenu />
              <IconButton
                color="inherit"
                size="small"
                sx={{ ml: 2 }}
                aria-label="apps"
                onClick={() => setAppsModalOpen(true)}
              >
                <AppsIcon />
              </IconButton>
              <IconButton
                component={Link}
                to="/help"
                color="inherit"
                size="small"
                sx={{ ml: 2 }}
                aria-label="help"
              >
                <HelpIcon />
              </IconButton>
              <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleProfileMenuClose}
              >
                <MenuItem disabled>
                  <Typography variant="body2">{user.email}</Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleEditProfile}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Edit Profile" />
                </MenuItem>
                <Divider />
                <MenuItem selected={presence.status === 'available'} onClick={async () => { 
                  try {
                    setPresence(p => ({ ...p, status: 'available', statusMessage: '' })); 
                    await presenceService.setStatus('available'); 
                    handleProfileMenuClose(); 
                  } catch (error) {
                    console.warn('Error setting available status:', error);
                  }
                }}>
                  <ListItemText primary="Set Available" />
                </MenuItem>
                <MenuItem selected={presence.status === 'away'} onClick={async () => { 
                  try {
                    setPresence(p => ({ ...p, status: 'away' })); 
                    await presenceService.setStatus('away'); 
                    handleProfileMenuClose(); 
                  } catch (error) {
                    console.warn('Error setting away status:', error);
                  }
                }}>
                  <ListItemText primary="Set Away" />
                </MenuItem>
                <MenuItem selected={presence.status === 'lunch'} onClick={async () => { 
                  try {
                    setPresence(p => ({ ...p, status: 'lunch', statusMessage: 'At lunch' })); 
                    await presenceService.setStatus('lunch', 'At lunch'); 
                    handleProfileMenuClose(); 
                  } catch (error) {
                    console.warn('Error setting lunch status:', error);
                  }
                }}>
                  <ListItemText primary="At Lunch" />
                </MenuItem>
                <MenuItem selected={presence.status === 'busy'} onClick={async () => { 
                  try {
                    setPresence(p => ({ ...p, status: 'busy', statusMessage: 'Busy' })); 
                    await presenceService.setStatus('busy', 'Busy'); 
                    handleProfileMenuClose(); 
                  } catch (error) {
                    console.warn('Error setting busy status:', error);
                  }
                }}>
                  <ListItemText primary="Set Busy" />
                </MenuItem>
                <MenuItem selected={presence.status === 'custom'} onClick={() => { setCustomStatusOpen(true); handleProfileMenuClose(); }}>
                  <ListItemText primary="Custom Status…" />
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </MenuItem>
              </Menu>

              {/* Custom Status Dialog */}
              <Dialog open={customStatusOpen} onClose={() => setCustomStatusOpen(false)}>
                <DialogTitle>Set Custom Status</DialogTitle>
                <DialogContent>
                  <TextField
                    autoFocus
                    margin="dense"
                    id="custom-status-message"
                    label="Status message"
                    type="text"
                    fullWidth
                    variant="standard"
                    value={customStatusMessage}
                    onChange={(e) => setCustomStatusMessage(e.target.value)}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setCustomStatusOpen(false)}>Cancel</Button>
                  <Button onClick={async () => { 
                    try {
                      setPresence(p => ({ ...p, status: 'custom', statusMessage: customStatusMessage })); 
                      await presenceService.setStatus('custom', customStatusMessage); 
                      setCustomStatusOpen(false); 
                      setCustomStatusMessage(''); 
                      handleProfileMenuClose(); 
                    } catch (error) {
                      console.warn('Error setting custom status:', error);
                    }
                  }}>Save</Button>
                </DialogActions>
              </Dialog>
              
              <Menu
                id="admin-menu"
                anchorEl={adminMenuAnchorEl}
                keepMounted
                open={Boolean(adminMenuAnchorEl)}
                onClose={handleAdminMenuClose}
              >
                {displayAdminNavItems
                  .filter(item => {
                    // Skip Google items for local users regardless of permissions
                    if (isGoogleItem(item) && !isGoogleUser()) {
                      return false;
                    }
                    const itemPermission = item.requiredPermission || item.permission;
                    return !itemPermission || hasPermission(itemPermission);
                  })
                  .map((item) => {
                    const itemText = item.friendlyName || item.title || item.text;
                    const itemPath = item.path;
                    
                    if (item.isDivider) {
                      return <Divider key={`divider-${itemText}`} />;
                    }

                    if (item.isHeader) {
                      return (
                        <Box key={`header-${itemText}`} sx={{ p: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            {itemText}
                          </Typography>
                        </Box>
                      );
                    }

                    return (
                      <MenuItem 
                        key={itemText} 
                        component={Link} 
                        to={itemPath}
                        onClick={handleAdminMenuClose}
                      >
                        <ListItemIcon>{renderIcon(item)}</ListItemIcon>
                        <ListItemText primary={itemText} />
                      </MenuItem>
                    );
                  })}
              </Menu>
              
              <Menu
                id="add-menu"
                anchorEl={addMenuAnchorEl}
                keepMounted
                open={Boolean(addMenuAnchorEl)}
                onClose={handleAddMenuClose}
              >
                {hasPermission('tickets:write') && (
                  <MenuItem
                    component={Link}
                    to="/tickets/new"
                    onClick={handleAddMenuClose}
                  >
                    <ListItemIcon>
                      <TicketIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Create Support Ticket" />
                  </MenuItem>
                )}

                {hasPermission('news:write') && (
                  <MenuItem
                    component={Link}
                    to="/news/posts/new"
                    onClick={handleAddMenuClose}
                  >
                    <ListItemIcon>
                      <NewsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Add News Item" />
                  </MenuItem>
                )}

                {hasPermission('forms:write') && (
                  <MenuItem
                    component={Link}
                    to="/forms/new"
                    onClick={handleAddMenuClose}
                  >
                    <ListItemIcon>
                      <FormsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Create Form" />
                  </MenuItem>
                )}

                {hasPermission('notes:write') && (
                  <MenuItem onClick={openQuickAddNote}>
                    <ListItemIcon>
                      <NotesIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Add Note" />
                  </MenuItem>
                )}

                {hasPermission('tasks:write') && (
                  <MenuItem
                    component={Link}
                    to="/tasks/new"
                    onClick={handleAddMenuClose}
                  >
                    <ListItemIcon>
                      <TaskManagementIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Add Task" />
                  </MenuItem>
                )}

                {(hasPermission('assets:write:own') || hasPermission('assets:write:any')) && (
                  <MenuItem
                    component={Link}
                    to="/assets/new"
                    onClick={handleAddMenuClose}
                  >
                    <ListItemIcon>
                      <AssetsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Add Asset" />
                  </MenuItem>
                )}
              </Menu>
            </>
          ) : null}
        </Toolbar>
      </AppBar>
      )}

      <Box sx={{ display: 'flex', flexGrow: 1, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
        {user && (
          <Drawer
            anchor="left"
            variant={isDesktop ? 'permanent' : 'temporary'}
            open={isDesktop ? true : drawerOpen}
            onClose={!isDesktop ? handleDrawerToggle : undefined}
            ModalProps={{ keepMounted: true }}
            sx={{
              width: isDesktop ? drawerWidth : 0,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
                transition: 'width 0.3s ease',
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                boxShadow: '4px 0 30px rgba(0, 0, 0, 0.1), inset -1px 0 0 rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                overflowX: 'hidden'
              }
            }}
          >
            {drawer}
          </Drawer>
        )}

        <Container component="main" maxWidth={false} sx={{
          flexGrow: 1,
          py: 3,
          pb: hasActiveChatTabs ? `${chatTaskbarHeight + 116}px` : '100px',
          width: '100%',
          maxWidth: '100%',
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4, lg: 4, xl: 6 },
          overflowX: 'hidden',
          background: 'transparent',
          '& a': { color: 'rgba(255, 255, 255, 0.98)' },
          // Do not force SVG color globally; let icons inherit from their closest colored ancestor
          '& svg': { color: 'inherit' },
          '& .MuiTypography-root': { color: 'inherit' },
          '& .MuiListItemText-primary, & .MuiListItemText-secondary': { color: 'inherit' },
          '& .MuiPaper-root': {
            color: 'text.primary',
            '& .MuiTypography-root': { color: 'inherit' },
            '& .MuiListItemText-primary, & .MuiListItemText-secondary': { color: 'inherit' },
            '& a': { color: 'primary.main' },
            '& svg': { color: 'text.secondary' }
          }
        }}>
          {user && routeRequiredPermission ? (
            <PermissionCheck requiredPermission={routeRequiredPermission}>
              <Outlet />
            </PermissionCheck>
          ) : (
            <Outlet />
          )}
        </Container>
      </Box>


      
      {/* Search Popup */}
      {user && (
        <Popover
          open={searchPopupOpen}
          anchorEl={searchAnchorEl}
          onClose={handleSearchClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{ mt: 1 }}
        >
          <Box sx={{ p: 2, minWidth: 400 }}>
            <SearchBar onClose={handleSearchClose} />
          </Box>
        </Popover>
      )}
      
      {/* Message Sidebar - only shown for authenticated users */}
      {user && <MessageSidebar />}
      
      {/* Apps Modal */}
      {user && <AppsModal open={appsModalOpen} onClose={() => setAppsModalOpen(false)} />}

      {/* Quick Add Note Dialog */}
      {user && (
        <Dialog open={addNoteOpen} onClose={() => setAddNoteOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>New Note</DialogTitle>
          <DialogContent>
            {addNoteError && (
              <Box sx={{ mt: 1 }}>
                <Alert severity="error">{addNoteError}</Alert>
              </Box>
            )}
            <TextField
              name="title"
              label="Title"
              fullWidth
              value={addNoteData.title}
              onChange={handleAddNoteInputChange}
              required
              margin="normal"
            />
            <TextField
              name="content"
              label="Content"
              fullWidth
              multiline
              rows={6}
              value={addNoteData.content}
              onChange={handleAddNoteInputChange}
              margin="normal"
            />
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isTodo"
                    checked={addNoteData.isTodo}
                    onChange={handleAddNoteInputChange}
                  />
                }
                label="This is a to-do item"
              />
              
              {addNoteData.isTodo && (
                <FormControlLabel
                  control={
                    <Checkbox
                      name="isCompleted"
                      checked={addNoteData.isCompleted}
                      onChange={handleAddNoteInputChange}
                    />
                  }
                  label="Mark as completed"
                />
              )}
              
              <FormControlLabel
                control={
                  <Checkbox
                    name="isPinned"
                    checked={addNoteData.isPinned}
                    onChange={handleAddNoteInputChange}
                  />
                }
                label="Pin this note"
              />
            </Box>
            
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={addNoteData.tags}
                onChange={(event, newValue) => {
                  setAddNoteData({
                    ...addNoteData,
                    tags: newValue
                  });
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Add tags"
                    helperText="Press Enter to add a tag"
                  />
                )}
              />
              
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={addNoteData.categories}
                onChange={(event, newValue) => {
                  setAddNoteData({
                    ...addNoteData,
                    categories: newValue
                  });
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      color="secondary"
                      variant="outlined"
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Categories"
                    placeholder="Add categories"
                    helperText="Press Enter to add a category"
                  />
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddNoteOpen(false)} disabled={addNoteSubmitting}>Cancel</Button>
            <Button onClick={handleAddNoteSubmit} variant="contained" disabled={!addNoteData.title || addNoteSubmitting}>
              {addNoteSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Offline Indicator */}
      <Snackbar
        open={!isOnline}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
          role="alert"
          aria-live="assertive"
        >
          You are offline. Some features may not work until connection is restored.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Layout;
